import { useState, useCallback, useRef, useEffect } from 'react';
import { Popup, getKeyHandler } from 't-lib-ui/structure';
import './SharePopup.css';

interface SharePopupState {
  title: string;
  url: string;
}

export function useShare(): { share: (title: string, url: string) => void; sharePopup: React.ReactNode } {
  const [state, setState] = useState<SharePopupState | null>(null);

  const share = useCallback((title: string, url: string) => {
    const isMobile = 'share' in navigator && matchMedia('(pointer: coarse) and (hover: none)').matches;
    if (isMobile) {
      navigator.share({ title, url }).catch(() => {});
      return;
    }
    setState({ title, url });
  }, []);

  const sharePopup = (
    <SharePopup
      show={!!state}
      title={state?.title ?? ''}
      url={state?.url ?? ''}
      onClose={() => setState(null)}
    />
  );

  return { share, sharePopup };
}

interface SharePopupProps {
  show: boolean;
  title: string;
  url: string;
  onClose: () => void;
}

const SharePopup: React.FC<SharePopupProps> = ({ show, title, url, onClose }) => {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLAnchorElement>(null);
  const copyRef = useRef<HTMLButtonElement>(null);

  // Copy is the common action, so focus it on open (instead of selecting the URL).
  useEffect(() => {
    if (show) {
      setCopied(false);
      requestAnimationFrame(() => copyRef.current?.focus());
    }
  }, [show, url]);

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const openPreview = () => window.open(url, '_blank', 'noopener,noreferrer');

  // Horizontal row [URL | Preview | Copy]. From the URL field, Down (or Right at the
  // end of the text) steps into the buttons; Left/Right move between them and Up/Left
  // returns to the field. Enter on the field copies.
  const inputKeyDown = getKeyHandler({
    enterBehaviour: 'action',
    spaceBehaviour: 'ignore',
    onAction: handleCopy,
    overrideHandlers: {
      ArrowDown: () => { previewRef.current?.focus(); return true; },
      ArrowRight: (el) => {
        const inp = el as HTMLInputElement;
        if (inp.selectionStart != inp.value.length || inp.selectionStart != inp.selectionEnd) return false;
        previewRef.current?.focus();
        return true;
      },
    },
  });
  const previewKeyDown = getKeyHandler({ sidewaysBehaviour: 'both', enterBehaviour: 'action', spaceBehaviour: 'action', onAction: openPreview });
  const copyKeyDown = getKeyHandler(handleCopy);

  return (
    <Popup show={show} title="Share" onClose={onClose} closable minWidth="480px" flowForm>
      <div className="sharePopupContent" onClick={e => e.stopPropagation()}>
        <p className="sharePopupText">Share <strong>{title.length > 64 ? title.slice(0, 64) + '...' : title}</strong> with this link:</p>
        <div className="sharePopupUrlRow">
          <input
            ref={inputRef}
            className="sharePopupUrlInput flowFocussable"
            value={url}
            readOnly
            onClick={() => inputRef.current?.select()}
            onKeyDown={inputKeyDown}
          />
          <a ref={previewRef} className="sharePopupOpenButton flowFocussable sidewayFocus" tabIndex={0} href={url} target="_blank" rel="noopener noreferrer" title="Preview link in a new tab" onKeyDown={previewKeyDown}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Preview
          </a>
          <button ref={copyRef} className="sharePopupCopyButton flowFocussable sidewayFocus" tabIndex={0} onClick={handleCopy} onKeyDown={copyKeyDown} title="Copy to clipboard">
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>
    </Popup>
  );
};

export default SharePopup;
